import React, { useEffect, useMemo, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix the default marker icon so the pin actually renders inside bundlers
// (react-leaflet relies on image assets Leaflet usually loads from a CDN).
const DEFAULT_ICON = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  const last = useRef({ lat: null, lng: null });
  useEffect(() => {
    // Only pan when the parent explicitly updates coords (e.g. preset buttons)
    if (last.current.lat === lat && last.current.lng === lng) return;
    last.current = { lat, lng };
    map.setView([lat, lng], map.getZoom(), { animate: true });
  }, [lat, lng, map]);
  return null;
}

/**
 * Interactive map picker for hyper-local campaigns.
 *   - Click anywhere to drop/move the pin
 *   - Drag the pin for fine adjustment
 *   - Live radius circle (km → metres) overlays the geofence
 */
export default function MapPicker({
  lat,
  lng,
  radiusKm,
  onChange,
  nearbyCampaigns = [],
  height = 320,
}) {
  const position = useMemo(() => [Number(lat) || 0, Number(lng) || 0], [lat, lng]);

  return (
    <div
      className="rounded-md overflow-hidden border border-border"
      style={{ height }}
      data-testid="cg-map-picker"
    >
      <MapContainer
        center={position}
        zoom={12}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter lat={position[0]} lng={position[1]} />
        <ClickHandler
          onPick={(la, ln) =>
            onChange?.({ latitude: Number(la.toFixed(6)), longitude: Number(ln.toFixed(6)) })
          }
        />

        {/* The clinic's own geofence */}
        <Circle
          center={position}
          radius={Math.max(50, Number(radiusKm) * 1000)}
          pathOptions={{ color: "#0b3d3a", weight: 2, fillOpacity: 0.08 }}
        />

        {/* Competing campaigns (from /api/clinic-growth/campaigns/nearby) */}
        {nearbyCampaigns
          .filter((c) => !c.is_mine)
          .map((c) => (
            <Circle
              key={c.id}
              center={[c.latitude, c.longitude]}
              radius={Math.max(50, Number(c.radius_km) * 1000)}
              pathOptions={{
                color: c.overlaps_boundary ? "#c45f4a" : "#94a3b8",
                weight: 1,
                dashArray: "4 4",
                fillOpacity: 0.05,
              }}
            />
          ))}

        {/* Draggable primary pin */}
        <Marker
          position={position}
          draggable
          icon={DEFAULT_ICON}
          eventHandlers={{
            dragend: (e) => {
              const { lat: la, lng: ln } = e.target.getLatLng();
              onChange?.({
                latitude: Number(la.toFixed(6)),
                longitude: Number(ln.toFixed(6)),
              });
            },
          }}
        />
      </MapContainer>
    </div>
  );
}
